'use client';

// CreatorMessageFlow - Multi-step dialog for sending a message to the bot's creator
// Story 4.11: Send a Message to My Creator

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslations } from 'next-intl';
import type { CreatorMessageStep, CreatorMessageData } from './useCreatorMessage';

interface CreatorMessageFlowProps {
  step: CreatorMessageStep;
  data: CreatorMessageData;
  onUpdateData: (partial: Partial<CreatorMessageData>) => void;
  onGoToStep: (step: CreatorMessageStep) => void;
  onSubmit: () => void;
  onClose: () => void;
  onReset: () => void;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CreatorMessageFlow({
  step,
  data,
  onUpdateData,
  onGoToStep,
  onSubmit,
  onClose,
  onReset,
}: CreatorMessageFlowProps) {
  const t = useTranslations('consumer.creatorMessage');

  if (step === 'closed') return null;

  // Email is optional but, if filled, must be valid. Phone is the alternative contact.
  const emailFilled = !!data.contactEmail;
  const emailIsValid = !emailFilled || emailRegex.test(data.contactEmail);
  const phoneIsValid = !!data.contactPhone && data.contactPhone.length >= 7;
  const hasValidContact = emailIsValid && (emailFilled || phoneIsValid);

  return (
    <Dialog open onClose={step === 'sending' ? undefined : onClose} maxWidth="sm" fullWidth>
      {/* Step: Message */}
      {step === 'message' && (
        <>
          <DialogTitle>{t('prompt')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={data.message}
              onChange={(e) => onUpdateData({ message: e.target.value.slice(0, 2000) })}
              placeholder={t('prompt')}
              inputProps={{ maxLength: 2000 }}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'end' }}>
              {data.message.length}/2000
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
            <Button
              onClick={() => onGoToStep('contact')}
              variant="contained"
              disabled={!data.message.trim()}
            >
              {t('next')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* Step: Contact */}
      {step === 'contact' && (
        <>
          <DialogTitle>{t('contactPrompt')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              type="email"
              label={t('emailLabel')}
              value={data.contactEmail}
              onChange={(e) => onUpdateData({ contactEmail: e.target.value })}
              error={emailFilled && !emailIsValid}
              helperText={emailFilled && !emailIsValid ? t('emailInvalid') : undefined}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              type="tel"
              label={t('phoneLabel')}
              value={data.contactPhone}
              onChange={(e) => onUpdateData({ contactPhone: e.target.value })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('contactHint')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
            <Button
              onClick={() => onGoToStep('name')}
              variant="contained"
              disabled={!hasValidContact}
            >
              {t('next')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* Step: Name */}
      {step === 'name' && (
        <>
          <DialogTitle>{t('namePrompt')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label={t('nameLabel')}
              value={data.contactName}
              onChange={(e) => onUpdateData({ contactName: e.target.value.slice(0, 100) })}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onGoToStep('confirm')} color="inherit">{t('skip')}</Button>
            <Button
              onClick={() => onGoToStep('confirm')}
              variant="contained"
            >
              {t('next')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* Step: Confirm */}
      {step === 'confirm' && (
        <>
          <DialogTitle>{t('confirmTitle')}</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.message}</Typography>
            </Box>
            {data.contactName && (
              <Typography variant="body2" color="text.secondary">
                {t('nameLabel')}: {data.contactName}
              </Typography>
            )}
            {data.contactEmail && (
              <Typography variant="body2" color="text.secondary">
                {t('emailLabel')}: {data.contactEmail}
              </Typography>
            )}
            {data.contactPhone && (
              <Typography variant="body2" color="text.secondary">
                {t('phoneLabel')}: {data.contactPhone}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
            <Button onClick={onSubmit} variant="contained">{t('send')}</Button>
          </DialogActions>
        </>
      )}

      {/* Step: Sending */}
      {step === 'sending' && (
        <>
          <DialogTitle>{t('sending')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </DialogContent>
        </>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon color="success" />
            {t('sent')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {t('defaultCompletion')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="contained">{t('close')}</Button>
          </DialogActions>
        </>
      )}

      {/* Step: Error */}
      {step === 'error' && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon color="error" />
            {t('errorTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">{t('error')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="inherit">{t('close')}</Button>
            <Button onClick={onReset} variant="contained">{t('retry')}</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
